import React, { useState, useCallback } from 'react';
import { Search, Sparkles, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { PageDocument } from './types';
import { inputStyle, labelStyle, fieldWrapStyle } from './seoPanelStyles';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Props {
  page: PageDocument | null;
  onPageSEOChange: (field: keyof PageDocument, value: string | boolean) => void;
}

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('/')) {
    return `${window.location.origin}${envUrl}`;
  }
  return envUrl || `${window.location.protocol}//${window.location.hostname}:8001/api`;
};

// Derive readable text from all sections on the current page for AI context
function extractPageText(page: PageDocument): string {
  const parts: string[] = [];
  parts.push(page.title);
  for (const section of page.sections) {
    const controls = section.controls || {};
    for (const [key, val] of Object.entries(controls)) {
      if (typeof val === 'string' && val.trim()) {
        // Include text-like controls; skip URLs, colours, booleans
        if (
          !key.includes('url') &&
          !key.includes('color') &&
          !key.includes('image') &&
          !key.includes('font') &&
          val.length < 1000
        ) {
          parts.push(val.trim());
        }
      }
      if (Array.isArray(val)) {
        for (const item of val) {
          if (typeof item === 'object' && item !== null) {
            for (const v of Object.values(item)) {
              if (typeof v === 'string' && v.trim() && v.length < 500) {
                parts.push(v.trim());
              }
            }
          }
        }
      }
    }
  }
  return parts.join(' ').slice(0, 2000);
}

const charCountColor = (current: number, max: number): string => {
  const ratio = current / max;
  if (ratio > 1) return '#ef4444';
  if (ratio > 0.9) return '#f59e0b';
  return '#94a3b8';
};

const SEOPanel: React.FC<Props> = ({ page, onPageSEOChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleGenerateSEO = useCallback(async () => {
    if (!page) return;
    setGenerating(true);
    setGenerateError('');
    try {
      const pageContent = extractPageText(page);
      const res = await axios.post(
        `${getApiBase()}/site-builder/generate-seo`,
        {
          page_content: pageContent,
          industry: '',
          focus_keyword: page.focus_keyword || '',
        },
        { headers, timeout: 90000 },
      );
      if (res.data.meta_title) {
        onPageSEOChange('meta_title', res.data.meta_title);
      }
      if (res.data.meta_description) {
        onPageSEOChange('meta_description', res.data.meta_description);
      }
    } catch (e: any) {
      setGenerateError(getApiError(e, 'AI generation failed'));
    } finally {
      setGenerating(false);
    }
  }, [page, headers, onPageSEOChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!page) {
    return null;
  }

  const metaTitle = page.meta_title || '';
  const metaDesc = page.meta_description || '';
  const ogImage = page.og_image || '';
  const canonicalUrl = page.canonical_url || '';
  const focusKeyword = page.focus_keyword || '';
  const noindex = !!page.noindex;

  return (
    <div style={{
      borderTop: '1px solid var(--stroke, rgba(15,23,42,0.10))',
      background: 'transparent',
    }}>
      {/* Section header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '10px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text)',
          fontSize: '0.8125rem',
          fontWeight: 600,
          textAlign: 'left',
        }}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <Search size={14} style={{ color: 'var(--text-secondary)' }} />
        <span>SEO Settings</span>
      </button>

      {!collapsed && (
        <div style={{ padding: '0 16px 16px' }}>
          {/* Generate with AI button */}
          <button
            onClick={handleGenerateSEO}
            disabled={generating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              width: '100%',
              padding: '7px 12px',
              marginBottom: 14,
              border: '1px solid var(--brand-primary, #2177FF)',
              borderRadius: 6,
              background: generating ? 'rgba(33,119,255,0.06)' : 'rgba(33,119,255,0.08)',
              color: 'var(--brand-primary, #2177FF)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: generating ? 'not-allowed' : 'pointer',
              opacity: generating ? 0.7 : 1,
              transition: 'background 0.15s',
            }}
          >
            <Sparkles size={14} />
            {generating ? 'Generating...' : 'Generate with AI'}
          </button>

          {generateError && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              marginBottom: 12,
              borderRadius: 6,
              background: 'rgba(239,68,68,0.08)',
              color: '#ef4444',
              fontSize: '0.75rem',
            }}>
              <AlertCircle size={13} />
              {generateError}
            </div>
          )}

          {/* Focus Keyword */}
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Focus Keyword</label>
            <input
              type="text"
              value={focusKeyword}
              placeholder="e.g. estate planning Melbourne"
              onChange={e => onPageSEOChange('focus_keyword', e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Meta Title */}
          <div style={fieldWrapStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Meta Title</label>
              <span style={{
                fontSize: '0.7rem',
                color: charCountColor(metaTitle.length, 60),
                fontVariantNumeric: 'tabular-nums',
              }}>
                {metaTitle.length}/60
              </span>
            </div>
            <input
              type="text"
              value={metaTitle}
              maxLength={70}
              placeholder={`${page.title} | Your Brand`}
              onChange={e => onPageSEOChange('meta_title', e.target.value)}
              style={{
                ...inputStyle,
                borderColor: metaTitle.length > 60 ? '#f59e0b' : undefined,
              }}
            />
            {metaTitle.length > 60 && (
              <p style={{ fontSize: '0.7rem', color: '#f59e0b', marginTop: 3 }}>
                Recommended max is 60 characters
              </p>
            )}
          </div>

          {/* Meta Description */}
          <div style={fieldWrapStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Meta Description</label>
              <span style={{
                fontSize: '0.7rem',
                color: charCountColor(metaDesc.length, 160),
                fontVariantNumeric: 'tabular-nums',
              }}>
                {metaDesc.length}/160
              </span>
            </div>
            <textarea
              value={metaDesc}
              maxLength={200}
              rows={3}
              placeholder="A compelling description that appears in search results..."
              onChange={e => onPageSEOChange('meta_description', e.target.value)}
              style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: 70,
                lineHeight: 1.5,
                fontFamily: 'inherit',
                borderColor: metaDesc.length > 160 ? '#f59e0b' : undefined,
              }}
            />
            {metaDesc.length > 160 && (
              <p style={{ fontSize: '0.7rem', color: '#f59e0b', marginTop: 3 }}>
                Recommended max is 160 characters
              </p>
            )}
          </div>

          {/* OG Image URL */}
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>OG Image URL</label>
            <input
              type="url"
              value={ogImage}
              placeholder="https://example.com/og-image.jpg"
              onChange={e => onPageSEOChange('og_image', e.target.value)}
              style={inputStyle}
            />
            <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
              Recommended: 1200 x 630 px
            </p>
          </div>

          {/* Canonical URL */}
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Canonical URL</label>
            <input
              type="url"
              value={canonicalUrl}
              placeholder="https://yourdomain.com/page"
              onChange={e => onPageSEOChange('canonical_url', e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* noindex */}
          <div style={fieldWrapStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text)' }}>
              <input
                type="checkbox"
                checked={noindex}
                onChange={e => onPageSEOChange('noindex', e.target.checked)}
              />
              Hide from search engines (noindex)
            </label>
            {noindex && (
              <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
                This page will be excluded from the sitemap and marked noindex, nofollow. Re-publish for this to take effect.
              </p>
            )}
          </div>

          {/* SERP preview */}
          {(metaTitle || metaDesc) && (
            <div style={{
              marginTop: 4,
              padding: 10,
              background: 'var(--panel-strong, rgba(241,245,249,0.6))',
              borderRadius: 6,
              border: '1px solid var(--stroke)',
            }}>
              <p style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                SERP Preview
              </p>
              <p style={{ fontSize: '0.8125rem', color: '#1a0dab', fontWeight: 500, marginBottom: 2, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {metaTitle || page.title}
              </p>
              {canonicalUrl && (
                <p style={{ fontSize: '0.75rem', color: '#006621', marginBottom: 3 }}>
                  {canonicalUrl.replace(/^https?:\/\//, '')}
                </p>
              )}
              <p style={{ fontSize: '0.8125rem', color: '#545454', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {metaDesc || 'No description set.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SEOPanel;
